/**
 * GPU Benchmark Utility for Helios
 *
 * Measures performance of different compute backends to help
 * users understand their system capabilities.
 */

import GPUEngine from './index';
import type { GPUBackend } from './index';
import { WebGPUEngine } from './webgpu-engine';
import { WebGLFallback } from './webgl-fallback';
import { CPUFallback } from './cpu-fallback';

export interface BenchmarkResult {
  name: string;
  backend: GPUBackend;
  time_ms: number;
  throughput: string;
  nodeCount?: number;
  nodesPerSecond?: number;
  textsPerSecond?: number;
}

export interface FullBenchmarkReport {
  backend: GPUBackend;
  capabilities: unknown;
  results: BenchmarkResult[];
  timestamp: string;
  totalTime_ms: number;
}

export interface BackendComparison {
  webgpu: BenchmarkResult[] | null;
  webgl: BenchmarkResult[] | null;
  cpu: BenchmarkResult[];
}

function generateTestNodes(count: number) {
  const nodes = [];
  for (let i = 0; i < count; i++) {
    nodes.push({
      id: `node-${i}`,
      x: (Math.random() - 0.5) * 1000,
      y: (Math.random() - 0.5) * 1000,
      mass: 1.0,
    });
  }
  return nodes;
}

function generateTestEdges(nodeCount: number, density: number = 0.01) {
  const edges = [];
  const edgeCount = Math.floor(nodeCount * density * nodeCount);
  for (let i = 0; i < edgeCount; i++) {
    edges.push({
      source: Math.floor(Math.random() * nodeCount),
      target: Math.floor(Math.random() * nodeCount),
    });
  }
  return edges;
}

export async function benchmarkForceLayout(
  nodeCount: number = 1000,
  iterations: number = 50
): Promise<BenchmarkResult> {
  await GPUEngine.init();
  const backend = GPUEngine.backend!;

  const nodes = generateTestNodes(nodeCount);
  const edges = generateTestEdges(nodeCount, 0.005);

  const start = performance.now();
  await GPUEngine.computeForceLayout(nodes, edges, iterations);
  const elapsed = performance.now() - start;

  const nodesPerSecond = Math.round((nodeCount * iterations) / (elapsed / 1000));

  return {
    name: `Force Layout (${nodeCount} nodes, ${iterations} iter)`,
    backend,
    time_ms: Math.round(elapsed * 100) / 100,
    throughput: `${nodesPerSecond.toLocaleString()} node-iterations/s`,
    nodeCount,
    nodesPerSecond,
  };
}

export async function benchmarkEmbeddings(
  vectorCount: number = 100,
  dim: number = 384
): Promise<BenchmarkResult> {
  await GPUEngine.init();
  const backend = GPUEngine.backend!;

  const vectorsA = new Float32Array(vectorCount * dim);
  const vectorsB = new Float32Array(vectorCount * dim);
  for (let i = 0; i < vectorsA.length; i++) {
    vectorsA[i] = Math.random();
    vectorsB[i] = Math.random();
  }

  const start = performance.now();
  await GPUEngine.computeEmbeddingSimilarity(vectorsA, vectorsB, dim);
  const elapsed = performance.now() - start;

  const comparisons = vectorCount * vectorCount;
  const compPerSecond = Math.round(comparisons / (elapsed / 1000));

  return {
    name: `Embedding Similarity (${vectorCount}x${vectorCount}, dim=${dim})`,
    backend,
    time_ms: Math.round(elapsed * 100) / 100,
    throughput: `${compPerSecond.toLocaleString()} comparisons/s`,
    textsPerSecond: compPerSecond,
  };
}

export async function benchmarkHeatmap(
  width: number = 512,
  height: number = 512
): Promise<BenchmarkResult> {
  await GPUEngine.init();
  const backend = GPUEngine.backend!;

  const data = new Float32Array(width * height);
  for (let i = 0; i < data.length; i++) {
    data[i] = Math.random() * 100;
  }

  const start = performance.now();
  await GPUEngine.computeHeatmap(data, width, height);
  const elapsed = performance.now() - start;

  const pixelsPerSecond = Math.round((width * height) / (elapsed / 1000));

  return {
    name: `Heatmap (${width}x${height})`,
    backend,
    time_ms: Math.round(elapsed * 100) / 100,
    throughput: `${pixelsPerSecond.toLocaleString()} pixels/s`,
  };
}

export async function benchmarkClustering(
  nodeCount: number = 200
): Promise<BenchmarkResult> {
  await GPUEngine.init();
  const backend = GPUEngine.backend!;

  // Generate random adjacency matrix
  const matrix = new Float32Array(nodeCount * nodeCount);
  for (let i = 0; i < nodeCount; i++) {
    for (let j = i + 1; j < nodeCount; j++) {
      const val = Math.random() < 0.05 ? 1 : 0;
      matrix[i * nodeCount + j] = val;
      matrix[j * nodeCount + i] = val;
    }
  }

  const start = performance.now();
  await GPUEngine.computeClusterDetection(matrix, 4);
  const elapsed = performance.now() - start;

  return {
    name: `Spectral Clustering (${nodeCount} nodes)`,
    backend,
    time_ms: Math.round(elapsed * 100) / 100,
    throughput: `${Math.round(nodeCount / (elapsed / 1000)).toLocaleString()} nodes/s`,
    nodeCount,
  };
}

export async function benchmarkAll(): Promise<FullBenchmarkReport> {
  const startTotal = performance.now();
  await GPUEngine.init();

  const results: BenchmarkResult[] = [];

  // Small graph
  results.push(await benchmarkForceLayout(500, 30));

  // Medium graph
  results.push(await benchmarkForceLayout(2000, 20));

  // Embeddings
  results.push(await benchmarkEmbeddings(50, 384));

  // Heatmap
  results.push(await benchmarkHeatmap(256, 256));

  // Clustering
  results.push(await benchmarkClustering(100));

  return {
    backend: GPUEngine.backend!,
    capabilities: GPUEngine.getCapabilities(),
    results,
    timestamp: new Date().toISOString(),
    totalTime_ms: Math.round(performance.now() - startTotal),
  };
}

export async function compareBackends(): Promise<BackendComparison> {
  const comparison: BackendComparison = {
    webgpu: null,
    webgl: null,
    cpu: [],
  };

  const testNodes = generateTestNodes(500);
  const testEdges = generateTestEdges(500, 0.005);
  const iterations = 20;

  // CPU (always available)
  try {
    const cpu = new CPUFallback();
    await cpu.init();
    const start = performance.now();
    await cpu.computeForceLayout(testNodes, testEdges, iterations);
    const elapsed = performance.now() - start;
    comparison.cpu.push({
      name: 'Force Layout (500 nodes)',
      backend: 'cpu',
      time_ms: Math.round(elapsed * 100) / 100,
      throughput: `${Math.round((500 * iterations) / (elapsed / 1000)).toLocaleString()} node-iter/s`,
      nodeCount: 500,
    });
    cpu.dispose();
  } catch (err) {
    console.warn('[Benchmark] CPU test failed:', err);
  }

  // WebGL
  try {
    const webgl = new WebGLFallback();
    await webgl.init();
    const start = performance.now();
    await webgl.computeForceLayout(testNodes, testEdges, iterations);
    const elapsed = performance.now() - start;
    comparison.webgl = [
      {
        name: 'Force Layout (500 nodes)',
        backend: 'webgl',
        time_ms: Math.round(elapsed * 100) / 100,
        throughput: `${Math.round((500 * iterations) / (elapsed / 1000)).toLocaleString()} node-iter/s`,
        nodeCount: 500,
      },
    ];
    webgl.dispose();
  } catch {
    // WebGL not available
  }

  // WebGPU
  if ('gpu' in navigator) {
    try {
      const webgpu = new WebGPUEngine();
      await webgpu.init();
      const start = performance.now();
      await webgpu.computeForceLayout(testNodes, testEdges, iterations);
      const elapsed = performance.now() - start;
      comparison.webgpu = [
        {
          name: 'Force Layout (500 nodes)',
          backend: 'webgpu',
          time_ms: Math.round(elapsed * 100) / 100,
          throughput: `${Math.round((500 * iterations) / (elapsed / 1000)).toLocaleString()} node-iter/s`,
          nodeCount: 500,
        },
      ];
      webgpu.dispose();
    } catch {
      // WebGPU not available
    }
  }

  return comparison;
}
